import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, poundsFromPence } from "../../lib/api";
import { SuperCard } from "../../components/SuperAdminLayout";
import { Building2, Ticket, PoundSterling, TrendingUp, CalendarDays, AlertTriangle } from "lucide-react";

export default function SuperOverviewPage() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/super/overview").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-[#8a8a8a] py-20 text-center">Loading platform metrics…</div>;

  const max = Math.max(1, ...d.sales_series.map((s) => s.total_pence));
  return (
    <div className="space-y-6" data-testid="super-overview">
      <h1 className="text-2xl font-bold">Platform overview</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Building2} label="Active attractions" value={d.active_attractions} sub={`${d.total_attractions} total`} />
        <Stat icon={Ticket} label="Tickets sold" value={d.total_tickets.toLocaleString()} />
        <Stat icon={PoundSterling} label="Gross sales" value={poundsFromPence(d.total_gross_pence)} />
        <Stat icon={TrendingUp} label="Platform commission" value={poundsFromPence(d.total_commission_pence)} highlight />
        <Stat icon={CalendarDays} label="Active events" value={d.active_events} />
        <Stat icon={Building2} label="New signups (month)" value={d.new_signups_month} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <SuperCard className="p-5 lg:col-span-2">
          <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-4">Commission · last 30 days</h3>
          {d.sales_series.length === 0 ? <div className="text-sm text-[#7a7a7a] py-8 text-center">No sales yet.</div> : (
            <div className="flex items-end gap-1 h-40" data-testid="super-sales-chart">
              {d.sales_series.map((s) => (
                <div key={s.date} className="flex-1 bg-[#dc2626]/80 rounded-t hover:bg-[#dc2626]" style={{ height: `${(s.total_pence / max) * 100}%` }} title={`${s.date}: ${poundsFromPence(s.total_pence)}`} />
              ))}
            </div>
          )}
        </SuperCard>
        <SuperCard className="p-5">
          <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-4">Needs attention</h3>
          <div className="space-y-2 text-sm">
            <Health label="Attractions without Stripe" value={d.health.no_stripe} />
            <Health label="Payment links expiring today" value={d.health.links_expiring_today} />
            <Health label="Swaps awaiting payment" value={d.health.swap_pending} />
          </div>
        </SuperCard>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <SuperCard className="p-5">
          <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-4">Top attractions</h3>
          <div className="space-y-1">
            {d.top_attractions.map((a, i) => (
              <Link key={a.id} to={`/super-admin/attractions/${a.id}`} className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-[#161616]" data-testid={`top-attraction-${a.id}`}>
                <span className="flex items-center gap-3"><span className="text-[#7a7a7a] w-4">{i + 1}</span>{a.name}</span>
                <span className="text-sm text-[#bbb]">{a.tickets_sold} tickets · {poundsFromPence(a.gross_pence)}</span>
              </Link>
            ))}
          </div>
        </SuperCard>
        <SuperCard className="p-5">
          <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-4">Recent signups</h3>
          <div className="space-y-1">
            {d.recent_signups.map((a) => (
              <Link key={a.id} to={`/super-admin/attractions/${a.id}`} className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-[#161616]">
                <span>{a.name}</span>
                <span className="text-xs text-[#7a7a7a]">{a.stripe_connected ? "Stripe ✓" : "No Stripe"} · {String(a.created_at).slice(0, 10)}</span>
              </Link>
            ))}
          </div>
        </SuperCard>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, highlight }) {
  return (
    <SuperCard className={`p-4 ${highlight ? "border-[#3a1414]" : ""}`}>
      <Icon size={18} className={highlight ? "text-[#dc2626]" : "text-[#8a8a8a]"} />
      <div className="text-2xl font-bold mt-2">{value}</div>
      <div className="text-xs text-[#8a8a8a]">{label}{sub ? ` · ${sub}` : ""}</div>
    </SuperCard>
  );
}
function Health({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#bbb]">{label}</span>
      <span className={`font-bold ${value > 0 ? "text-[#facc15]" : "text-[#86efac]"}`}>{value}</span>
    </div>
  );
}
